import React, { useEffect, useState, useMemo } from "react";
import {
    Container, Card, CardBody, Table, Button,
    Modal, ModalHeader, ModalBody, FormGroup, Label, Input, Spinner
} from "reactstrap";
import Swal from "sweetalert2";
import { FaPlus, FaTrash, FaEdit, FaSave } from "react-icons/fa";

export function ImportListMemberAnnounce4Component() {
    const user = JSON.parse(localStorage.getItem("userData"));

    const [receiptList, setReceiptList] = useState([]);
    const [peopleList, setPeopleList] = useState([]);
    const [loadingPeople, setLoadingPeople] = useState(false);

    const [selectedAllowCodes, setSelectedAllowCodes] = useState(() => new Set());
    const [selectedPeople, setSelectedPeople] = useState(() => new Map());

    const [receiptNo, setReceiptNo] = useState("");
    const [searchText, setSearchText] = useState("");
    const [modalOpen, setModalOpen] = useState(false);

    const [isEdit, setIsEdit] = useState(false);

    /* ================= INIT ================= */
    useEffect(() => {
        loadReceipts();
        loadPeople();
    }, []);

    /* ================= LOAD ================= */
    const loadReceipts = async () => {
        const res = await fetch("/api/SupportForm/listReceipt", {
            headers: { token: user.token }
        });
        const json = await res.json();
        setReceiptList(json.dataenum || []);
    };

    const loadPeople = async () => {
        setLoadingPeople(true);
        try {
            const res = await fetch("/api/SupportForm/listMemberAnnounce4", {
                headers: { token: user.token }
            });
            const json = await res.json();
            setPeopleList(json.dataenum || []);
        } finally {
            setLoadingPeople(false);
        }
    };

    /* ================= MODAL ================= */
    const openAddModal = () => {
        setIsEdit(false);
        setReceiptNo("");
        setSearchText("");
        setSelectedAllowCodes(new Set());
        setSelectedPeople(new Map());
        setModalOpen(true);
    };

    const openEditModal = async (receiptNo) => {
        setIsEdit(true);
        setReceiptNo(receiptNo);
        setModalOpen(true);

        const res = await fetch(`/api/SupportForm/getReceiptAnnounce4Detail?receiptNo=${receiptNo}`,
            { headers: { token: user.token } }
        );
        const json = await res.json();

        const allowSet = new Set();
        const peopleMap = new Map();

        json.dataenum.forEach(p => {
            allowSet.add(p.allowCode);
            peopleMap.set(p.name, {
                personId: p.personId,
                name: p.name,
                allowCode: p.allowCode,
                lawOccupationName: p.lawOccupationName,
                testOccupationName: p.testOccupationName
            });
        });

        setSelectedAllowCodes(allowSet);
        setSelectedPeople(peopleMap);
    };


    /* ================= ALLOW CODE ================= */
    const allowCodeList = useMemo(() => {
        const map = new Map();
        for (const p of peopleList) {
            if (!p.allowCode) continue;
            if (!map.has(p.allowCode)) {
                map.set(p.allowCode, {
                    allowCode: p.allowCode,
                    lawOccupationName: p.lawOccupationName,
                    testOccupationName: p.testOccupationName
                });
            }
        }
        return Array.from(map.values());
    }, [peopleList]);

    const toggleAllowCode = (code) => {
        setSelectedAllowCodes(prev => {
            const next = new Set(prev);
            next.has(code) ? next.delete(code) : next.add(code);
            return next;
        });
    };

    /* ================= PEOPLE FILTER ================= */
    const filteredPeople = useMemo(() => {
        if (selectedAllowCodes.size === 0) return [];
        return peopleList.filter(p =>
            selectedAllowCodes.has(p.allowCode) &&
            (
                (p.personId ?? "").includes(searchText) ||
                (p.name ?? "").includes(searchText)
            )
        );
    }, [peopleList, selectedAllowCodes, searchText]);

    /* ================= TOGGLE PERSON ================= */
    const togglePerson = (p) => {
        setSelectedPeople(prev => {
            const next = new Map(prev);
            if (next.has(p.name)) {
                next.delete(p.name);
            } else {
                next.set(p.name, {
                    personId: p.personId,
                    name: p.name,
                    allowCode: p.allowCode,
                    lawOccupationName: p.lawOccupationName,
                    testOccupationName: p.testOccupationName
                });
            }
            return next;
        });
    };

    /* ================= SAVE (INSERT / UPDATE) ================= */
    const handleSave = async () => {
        if (!receiptNo) {
            Swal.fire("กรุณากรอกเลขที่ใบเสร็จ", "", "warning");
            return;
        }

        if (selectedPeople.size === 0) {
            Swal.fire("กรุณาเลือกรายชื่ออย่างน้อย 1 คน", "", "warning");
            return;
        }

        const payload = {
            receiptAnnounce4Id: isEdit ? 1 : 0,
            receiptNo,
            createBy: user.fullName,
            people: Array.from(selectedPeople.values())
        };

        const res = await fetch("/api/SupportForm/saveReceiptAnnounce4", {
            method: "POST",
            headers: {
                "Content-Type": "application/json",
                token: user.token
            },
            body: JSON.stringify(payload)
        });

        const json = await res.json();
        if (json.status == "1") {
            Swal.fire("สำเร็จ", isEdit ? "แก้ไขข้อมูลเรียบร้อย" : "บันทึกข้อมูลเรียบร้อย", "success");
            setModalOpen(false);
            loadReceipts();
        } else {
            Swal.fire("ไม่สำเร็จ", json.message, "error");
        }
    };

    /* ================= DELETE ================= */
    const handleDelete = (receiptNo) => {
        Swal.fire({
            title: "ยืนยันการลบ?",
            text: `ต้องการลบใบเสร็จ ${receiptNo}`,
            icon: "warning",
            showCancelButton: true
        }).then(async r => {
            if (!r.isConfirmed) return;

            const res = await fetch("/api/SupportForm/deleteReceiptAnnounce4", {
                method: "POST",
                headers: {
                    "Content-Type": "application/json",
                    token: user.token
                },
                body: JSON.stringify({ receiptNo })
            });

            const json = await res.json();
            if (json.status == "1") {
                Swal.fire("ลบสำเร็จ", "", "success");
                loadReceipts();
            } else {
                Swal.fire("ไม่สำเร็จ", json.message, "error");
            }
        });
    };

    /* ================= RENDER ================= */
    return (
        <Container className="mt-4">
            <Card>
                <CardBody>
                    <div className="d-flex justify-content-end mb-3">
                        <Button color="primary" onClick={openAddModal}>
                            <FaPlus className="me-2" /> เพิ่มใบเสร็จ
                        </Button>
                    </div>

                    <Table bordered>
                        <thead>
                            <tr>
                                <th>เลขที่ใบเสร็จ</th>
                                <th>วันที่สร้าง</th>
                                <th>ผู้สร้าง</th>
                                <th>วันที่แก้ไข</th>
                                <th>ผู้แก้ไข</th>
                                <th></th>
                            </tr>
                        </thead>
                        <tbody>
                            {receiptList.map(r => (
                                <tr key={r.receiptNo}>
                                    <td>{r.receiptNo}</td>
                                    <td>{r.createDate}</td>
                                    <td>{r.createBy}</td>
                                    <td>{r.updateDate}</td>
                                    <td>{r.updateBy}</td>
                                    <td className="text-center">

                                        <FaEdit
                                            className="mx-1 style-icon"
                                            onClick={() => {
                                                openEditModal(r.receiptNo)
                                            }}
                                        />
                                        <FaTrash
                                            className="mx-1 style-icon"
                                            onClick={() => {
                                                handleDelete(r.receiptNo)
                                            }}
                                        />

                                    </td>


                                </tr>
                            ))}
                        </tbody>
                    </Table>
                </CardBody>
            </Card>

            {/* ================= MODAL ================= */}
            <Modal size="xl" isOpen={modalOpen} toggle={() => setModalOpen(false)}>
                <ModalHeader>{isEdit ? "แก้ไขใบเสร็จ" : "เพิ่มใบเสร็จ"}</ModalHeader>
                <ModalBody>
                    <FormGroup>
                        <Label>เลขที่ใบเสร็จ</Label>
                        <Input value={receiptNo} disabled={isEdit}
                            onChange={e => setReceiptNo(e.target.value)} />
                    </FormGroup>

                    <h6>เลือกเลขวุฒิบัตร</h6>
                    <div className="d-flex flex-wrap gap-2 mb-3">
                        {allowCodeList.map(a => (
                            <label key={a.allowCode}
                                className={`border rounded p-2 ${selectedAllowCodes.has(a.allowCode) ? "bg-light border-primary" : ""}`}>
                                <Input type="checkbox"
                                    checked={selectedAllowCodes.has(a.allowCode)}
                                    onChange={() => toggleAllowCode(a.allowCode)} />
                                <strong className="ms-2">{a.allowCode}</strong>
                            </label>
                        ))}
                    </div>

                    {selectedAllowCodes.size > 0 && (
                        <>
                            <Input className="mb-2"
                                placeholder="ค้นหาเลขบัตร / ชื่อ"
                                value={searchText}
                                onChange={e => setSearchText(e.target.value)} />

                            <div style={{ maxHeight: 300, overflowY: "auto" }}>
                                <Table bordered>
                                    <thead>
                                        <tr>
                                            <th></th>
                                            <th>เลขบัตร</th>
                                            <th>ชื่อ</th>
                                            <th>สาขา</th>
                                            <th>สาขาทดสอบ</th>
                                        </tr>
                                    </thead>
                                    <tbody>
                                        {filteredPeople.map((p, i) => (
                                            <tr key={`${p.name}-${i}`}>
                                                <td>
                                                    <Input type="checkbox"
                                                        checked={selectedPeople.has(p.name)}
                                                        onChange={() => togglePerson(p)} />
                                                </td>
                                                <td>{p.personId}</td>
                                                <td>{p.name}</td>
                                                <td>{p.lawOccupationName}</td>
                                                <td>{p.testOccupationName}</td>
                                            </tr>
                                        ))}
                                    </tbody>
                                </Table>
                            </div>
                        </>
                    )}

                    <div className="text-center mt-3">
                        <Button color="success" onClick={handleSave}>
                            <FaSave className="me-2" /> บันทึก
                        </Button>
                    </div>
                </ModalBody>
            </Modal>
        </Container>
    );
}
